import api from './api';

const studentService = {
  getStudents: async () => {
    try {
      const response = await api.get('/students');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  addStudent: async (studentData) => {
    try {
      // Validate required fields (no resume field)
      if (!studentData.name || !studentData.email || !studentData.phone) {
        throw new Error('Name, email, and phone are required');
      }

      const response = await api.post('/students', {
        name: studentData.name.trim(),
        email: studentData.email.trim().toLowerCase(),
        phone: studentData.phone.trim()
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get student by token (for slot selection)
  getStudentByToken: async (token) => {
    try {
      if (!token) {
        throw new Error('Token is required');
      }
      const response = await api.get(`/students/by-token/${token}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update resume by token (for slot selection)
  updateResume: async (token, file) => {
    try {
      if (!token) {
        throw new Error('Token is required');
      }
      if (!file) {
        throw new Error('Resume file is required');
      }

      const formData = new FormData();
      formData.append('resume', file);
      
      const response = await api.put(`/students/resume/${token}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Download resume method
  downloadResume: async (studentId) => {
    try {
      if (!studentId) {
        throw new Error('Student ID is required');
      }

      const response = await api.get(`/students/${studentId}/resume/download`, {
        responseType: 'blob',
        timeout: 30000,
      });

      // Enhanced filename extraction from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'resume.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return {
        blob: response.data,
        filename: filename,
        contentType: response.headers['content-type'] || 'application/octet-stream',
        size: parseInt(response.headers['content-length']) || response.data.size
      };
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Resume not found or student does not exist');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid student ID provided');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Download timeout. Please try again.');
      }
      
      throw error;
    }
  },

  // Bulk download resumes as ZIP file
  downloadBulkResumes: async (studentIds) => {
    try {
      if (!Array.isArray(studentIds) || studentIds.length === 0) {
        throw new Error('Student IDs array is required');
      }

      const response = await api.post('/students/bulk-download-resumes', 
        { studentIds },
        {
          responseType: 'blob',
          timeout: 120000,
        }
      );

      // Extract ZIP filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Student_Resumes_${new Date().toISOString().split('T')[0]}.zip`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return {
        blob: response.data,
        filename: filename,
        contentType: 'application/zip',
        size: parseInt(response.headers['content-length']) || response.data.size
      };
    } catch (error) {
      throw error;
    }
  },

  // Get student interview status
  getStudentStatus: async (studentId) => {
    try {
      if (!studentId) {
        throw new Error('Student ID is required');
      }

      const response = await api.get(`/students/${studentId}/status`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  downloadSampleExcel: async () => {
    try {
      const response = await api.get('/students/sample-template', { 
        responseType: 'blob' 
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  uploadExcel: async (file) => {
    try {
      if (!file) {
        throw new Error('No file selected');
      }

      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];

      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please select an Excel file (.xlsx or .xls)');
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size too large. Maximum size is 10MB');
      }

      const formData = new FormData();
      formData.append('excel', file);

      const response = await api.post('/students/upload-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000,
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // OLD METHOD - Keep for backward compatibility but deprecated
  sendInvitations: async (studentIds) => {
    console.warn('⚠️ sendInvitations is deprecated. Use sendBulkInvitations instead.');
    return studentService.sendBulkInvitations(studentIds);
  },

  // NEW: Send Bulk Invitations with Sequential Processing
  sendBulkInvitations: async (studentIds) => {
    try {
      if (!Array.isArray(studentIds) || studentIds.length === 0) {
        throw new Error('studentIds must be a non-empty array');
      }

      const validIds = studentIds.filter(id => typeof id === 'number' && !isNaN(id));
      if (validIds.length === 0) {
        throw new Error('No valid student IDs to send invitations');
      }

      if (validIds.length > 50) {
        throw new Error('Cannot send more than 50 invitations at once');
      }

      console.log(`📧 Requesting bulk invitations for ${validIds.length} students`);
      
      const response = await api.post('/students/send-bulk-invitations', {
        studentIds: validIds
      }, {
        timeout: 300000, // 5 minutes timeout for bulk operations
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Bulk invitation response:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Bulk invitation request failed:', error);
      
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Server error occurred';
        throw new Error(errorMessage);
      } else if (error.request) {
        // Request timeout or network error
        throw new Error('Network error or request timeout. Please try again.');
      } else {
        // Other error
        throw new Error(error.message || 'Failed to send bulk invitations');
      }
    }
  },

  deleteStudent: async (id) => {
    try {
      const response = await api.delete(`/students/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Bulk delete students by date range
  deleteByDate: async (start, end) => {
    try {
      if (!start || !end) {
        throw new Error('Start and end dates are required');
      }
      const response = await api.delete(`/students/bulk?start=${start}&end=${end}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Validate resume file before upload
  validateResumeFile: (file) => {
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 5 * 1024 * 1024;

    if (!file) {
      throw new Error('No file provided');
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Only PDF, DOC, and DOCX files are allowed');
    }

    if (file.size > maxSize) {
      throw new Error('File size too large. Maximum size is 5MB');
    }

    return true;
  },

  // Helper method to format file size
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Helper method to trigger file download in browser
  triggerDownload: (blob, filename) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      return true;
    } catch (error) {
      return false;
    }
  },

  // Comprehensive resume download with UI feedback
  downloadResumeWithFeedback: async (studentId, studentName) => {
    try {
      const { blob, filename, contentType, size } = await studentService.downloadResume(studentId);
      
      // Trigger browser download
      const downloadSuccess = studentService.triggerDownload(blob, filename);
      
      if (!downloadSuccess) {
        throw new Error('Failed to trigger file download');
      }
      
      return {
        success: true,
        filename,
        size: size || blob.size,
        contentType,
        studentName
      };
      
    } catch (error) {
      throw error;
    }
  },

  // Batch resume downloads with improved feedback
  downloadMultipleResumes: async (students) => {
    const results = [];
    let successCount = 0;
    let failCount = 0;
    
    for (let i = 0; i < students.length; i++) {
      const student = students[i];
      try {
        const result = await studentService.downloadResumeWithFeedback(student.id, student.name);
        results.push({
          studentId: student.id,
          studentName: student.name,
          success: true,
          filename: result.filename,
          size: result.size
        });
        successCount++;
        
        // Add progressive delay between downloads
        const delay = Math.min(500 + (i * 50), 2000);
        await new Promise(resolve => setTimeout(resolve, delay));
        
      } catch (error) {
        results.push({
          studentId: student.id,
          studentName: student.name,
          success: false,
          error: error.message
        });
        failCount++;
        
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    
    return {
      summary: {
        total: students.length,
        successful: successCount,
        failed: failCount
      },
      results
    };
  },

  // Bulk resume download as ZIP file
  downloadBulkResumesAsZip: async (students) => {
    try {
      if (!Array.isArray(students) || students.length === 0) {
        throw new Error('Students array is required');
      }

      const studentIds = students.map(s => s.id || s.Id);
      const { blob, filename, size } = await studentService.downloadBulkResumes(studentIds);
      
      // Trigger browser download
      const downloadSuccess = studentService.triggerDownload(blob, filename);
      
      if (!downloadSuccess) {
        throw new Error('Failed to trigger ZIP download');
      }
      
      return {
        success: true,
        filename,
        size,
        studentsCount: students.length
      };
      
    } catch (error) {
      throw error;
    }
  },

  // Check if student has resume
  hasResume: (student) => {
    return !!(student.ResumeLink || student.resumeLink);
  },

  // Get students with resumes from selection
  getStudentsWithResumes: (students, selectedIds) => {
    return students.filter(student => 
      selectedIds.includes(student.Id || student.id) && 
      studentService.hasResume(student)
    );
  },

  // Resume download options for UI
  getDownloadOptions: (selectedStudents, allStudents) => {
    const studentsWithResumes = selectedStudents.filter(id => {
      const student = allStudents.find(s => s.Id === id || s.id === id);
      return student && studentService.hasResume(student);
    });

    return {
      totalSelected: selectedStudents.length,
      withResumes: studentsWithResumes.length,
      withoutResumes: selectedStudents.length - studentsWithResumes.length,
      canDownload: studentsWithResumes.length > 0,
      recommendZip: studentsWithResumes.length > 5
    };
  }
};

export default studentService;
